import { useCallback, useEffect, useState } from 'react'
import { feedApi } from '../api'
import type { FeedPost } from '../api/types'
import { useAuth } from '../context/AuthContext'
import { useToast } from '../context/ToastContext'
import { Button } from './ui/Button'

const MAX = 500

function timeAgo(ts: number): string {
  const diff = Math.max(0, Date.now() - ts) / 1000
  if (diff < 60) return 'gerade eben'
  if (diff < 3600) return `vor ${Math.floor(diff / 60)} Min.`
  if (diff < 86400) return `vor ${Math.floor(diff / 3600)} Std.`
  return new Date(ts).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' })
}

export function FeedPanel() {
  const { user } = useAuth()
  const toast = useToast()
  const [posts, setPosts] = useState<FeedPost[] | null>(null)
  const [text, setText] = useState('')
  const [sending, setSending] = useState(false)

  const load = useCallback(async () => {
    try {
      setPosts((await feedApi.list()).posts)
    } catch {
      /* keep the previous list on transient errors */
    }
  }, [])

  useEffect(() => {
    void load()
    const t = window.setInterval(() => { void load() }, 15000)
    return () => window.clearInterval(t)
  }, [load])

  async function submit() {
    const value = text.trim()
    if (!value) return
    setSending(true)
    try {
      const { post } = await feedApi.create(value)
      setPosts((prev) => [post, ...(prev ?? [])])
      setText('')
    } catch {
      toast.push('Beitrag konnte nicht gesendet werden', 'error')
    } finally {
      setSending(false)
    }
  }

  async function remove(id: string) {
    try {
      await feedApi.remove(id)
      setPosts((prev) => (prev ?? []).filter((p) => p.id !== id))
    } catch {
      toast.push('Löschen fehlgeschlagen', 'error')
    }
  }

  return (
    <aside className="feed">
      <div className="feed-head">
        <h2>Neuigkeiten</h2>
        <span className="muted">für alle in der Familie</span>
      </div>
      <form
        className="feed-compose"
        onSubmit={(e) => {
          e.preventDefault()
          void submit()
        }}
      >
        <textarea
          className="input"
          rows={2}
          maxLength={MAX}
          placeholder="z. B. Komme 5 Minuten später zum Videocall …"
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
              e.preventDefault()
              void submit()
            }
          }}
        />
        <div className="feed-compose-row">
          <span className="muted">{text.length}/{MAX}</span>
          <Button size="sm" type="submit" loading={sending} disabled={!text.trim()}>
            Posten
          </Button>
        </div>
      </form>
      <ul className="feed-list">
        {posts === null ? (
          <li className="muted">Lädt …</li>
        ) : posts.length === 0 ? (
          <li className="muted">Noch keine Beiträge.</li>
        ) : (
          posts.map((p) => (
            <li key={p.id} className="feed-post">
              <span className="avatar" aria-hidden="true">
                {p.displayName.trim().charAt(0).toUpperCase() || '?'}
              </span>
              <div className="feed-post-body">
                <div className="feed-post-meta">
                  <strong>{p.displayName}</strong>
                  <time dateTime={new Date(p.ts).toISOString()}>{timeAgo(p.ts)}</time>
                  {user && (user.id === p.userId || user.role === 'ADMIN') ? (
                    <button className="feed-del" onClick={() => remove(p.id)} aria-label="Beitrag löschen">
                      ×
                    </button>
                  ) : null}
                </div>
                <p>{p.text}</p>
              </div>
            </li>
          ))
        )}
      </ul>
    </aside>
  )
}
